// Mission list service
import dayjs from 'dayjs';
import { isRequestCancellation } from '@/utils/https';
import {
  fetchMissionList,
  fetchMissionDetail,
  fetchMissionEnvelopeUsage,
  fetchMissionFreelancers,
  fetchMissionTeamMembers,
  createMissionApi,
  updateMissionApi,
  assignFreelancerApi,
  deleteMissionApi,
  uploadMissionDocumentsApi,
  fetchMissionTemplatePreviewApi,
  uploadOtherDocumentsApi,
  editMissionDocumentsApi,
  deleteDocumentApi,
  extendMissionApi,
  terminateMissionApi,
  patchScheduledTerminationApi,
  acceptMissionApi,
  rejectMissionApi,
  reviewMissionApprovalApi,
  cancelMissionApi,
  acceptAddendumApi,
  rejectAddendumApi,
  cancelAddendumApi,
  fetchFreelancerMasterAgreement,
  initiateSignatureRequestApi,
  rejectSignableApi,
} from '@/apis/missions';
import { CONTRACT_TYPE_SERVICES_AGREEMENT } from '@/constants/mission';
import { formatDate, DATE_FORMATS } from '@/utils/dateFormatter';
import { getCompanySignatureSignerIds } from '@/utils/companySignatureSigners';
import { formatCurrency } from '@/utils/currencyFormatter';
import { DEFAULT_CURRENCY } from '@/data/currencies';

// NOTE: normalizeMissionStatus has been removed - all status calculation is now done in backend
// See: packages/nextjs/lib/utils/missionDisplayStatus.ts::calculateMissionDisplayStatus()

const normalizePricingType = (missionType) => {
  const map = {
    hourly: 'hourly',
    daily: 'daily',
    monthly: 'monthly',
    deliverable: 'deliverable',
  };
  const value = String(missionType || '').toLowerCase();
  return map[value] || value || 'deliverable';
};

const formatTimeline = (start, end) => {
  if (!start || !end) return '- -';
  const formattedStart = formatDate(start, DATE_FORMATS.MEDIUM);
  const formattedEnd = formatDate(end, DATE_FORMATS.MEDIUM);
  if (formattedStart === '--' || formattedEnd === '--') return '- -';
  return `${formattedStart} - ${formattedEnd}`;
};

const formatRate = (missionType, rateAmount, currency = DEFAULT_CURRENCY) => {
  if (rateAmount === null || rateAmount === undefined) return '- -';
  const unitMap = {
    hourly: 'hour',
    daily: 'day',
    monthly: 'month',
    deliverable: 'deliverable',
  };
  const unit = unitMap[normalizePricingType(missionType)] || 'deliverable';
  // Format: <montant devise> / per unit (sera traduit dans le composant)
  return `${formatCurrency(rateAmount, currency)} / per ${unit}`;
};

const formatUserName = (user) => {
  if (!user) return '--';
  if (typeof user === 'string') return user || '--';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.email || '--';
};

const normalizeFreelancerOptions = (items = []) =>
  items.map((item) => ({
    id: item?.id,
    name: formatUserName(item),
    is_pending: item?.is_pending || false,
    is_on_mission: item?.is_on_mission || false,
    email: item?.email || '',
    company_name: item?.company_name || '',
    company_title: item?.company_title || '',
    role: item?.role || 'Freelancer',
    referent_id: item?.referent_id || null,
    has_valid_master_agreement: item?.has_valid_master_agreement || false,
  }));

const normalizeTeamMemberOptions = (items = []) =>
  items.map((item) => {
    const id = item?.id ?? item?.user_id ?? item?.email;
    const name = formatUserName(item);
    return {
      id,
      value: id,
      label: name,
      name,
      first_name: item?.first_name ?? '',
      last_name: item?.last_name ?? '',
      email: item?.email ?? '',
      avatar: item?.avatar ?? null,
      role: item?.role ?? '',
    };
  });

const normalizeApiMissions = (items = []) =>
  items.map((item) => {
    const missionType = normalizePricingType(item?.mission_type);
    const rawStatus = item?.status || '';
    
    // ONLY use display_status from backend - no client-side calculation
    // The backend calculates this using the centralized function:
    // packages/nextjs/lib/utils/missionDisplayStatus.ts::calculateMissionDisplayStatus()
    // The backend MUST always return display_status - if not, this is a critical error
    if (!item?.display_status) {
      console.error(
        '[normalizeApiMissions] CRITICAL: Backend did not provide display_status for mission:',
        item?.id || 'unknown',
        'Status:', item?.status,
        'This is a backend bug - display_status should always be calculated server-side.'
      );
      // Use pending-freelancer-review as fallback instead of awaiting-freelancer-acceptance
      // This is more accurate as it indicates action is needed
      const displayStatus = 'pending-freelancer-review';
      return {
        id: item?.id,
        title: item?.name || '--',
        pricingType: missionType,
        timeline: formatTimeline(item?.start_date, item?.end_date),
        currency: item?.currency || DEFAULT_CURRENCY,
        rateAmount: item?.rate_amount ?? null,
        budget:
          item?.rate_amount !== null && item?.rate_amount !== undefined
            ? formatCurrency(item.rate_amount, item?.currency || DEFAULT_CURRENCY)
            : undefined,
        rate: formatRate(item?.mission_type, item?.rate_amount, item?.currency || DEFAULT_CURRENCY),
        freelancer: formatUserName(item?.freelancer),
        freelancerData: item?.freelancer || null,
        creator: formatUserName(item?.created_by),
        approver: formatUserName(item?.approver),
        approver_id:
          item?.approver_id ??
          (item?.approver && typeof item.approver === 'object' ? item.approver.id : null) ??
          null,
        approval_status: item?.approval_status ?? null,
        signor: formatUserName(item?.team_signer),
        team_signer: item?.team_signer || null,
        team_signer_id: item?.team_signer_id ?? item?.team_signer?.id ?? null,
        companySignatureSignerIds: getCompanySignatureSignerIds(item),
        company_signature_signer_ids: getCompanySignatureSignerIds(item),
        companySignatureSignerId: item?.company_signature_signer_id ?? item?.companySignatureSignerId ?? null,
        companySignatureSigner: item?.company_signature_signer || item?.companySignatureSigner || null,
        status: rawStatus,
        displayStatus: displayStatus,
        purchase_order: item?.purchase_order || null,
        project_code_id: item?.project_code_id || null,
        project_code: item?.project_code || null,
        pending_addenda_count: item?.pending_addenda_count || 0,
        pending_addenda_count_for_company: item?.pending_addenda_count_for_company || 0,
        pending_documents_for_freelancer_count: item?.pending_documents_for_freelancer_count ?? 0,
        pending_documents_for_company_count: item?.pending_documents_for_company_count ?? 0,
        extension_proposed_end_date: item?.extension_proposed_end_date ?? null,
        extensionProposedEndDate: item?.extension_proposed_end_date ?? null,
        start_date: item?.start_date || null,
        end_date: item?.end_date || null,
        start_time: item?.start_time ?? null,
        end_time: item?.end_time ?? null,
        startTime: item?.start_time ?? null,
        endTime: item?.end_time ?? null,
        termination_effective_date: item?.termination_effective_date ?? null,
        termination_contract_end_date: item?.termination_contract_end_date ?? null,
        terminationEffectiveDate: item?.termination_effective_date ?? null,
        terminationContractEndDate: item?.termination_contract_end_date ?? null,
        freelancer_legal_entity_type: item?.freelancer_legal_entity_type ?? null,
        freelancerLegalEntityType: item?.freelancer_legal_entity_type ?? null,
      };
    }

    const displayStatus = item.display_status;
    return {
      id: item?.id,
      title: item?.name || '--',
      pricingType: missionType,
      timeline: formatTimeline(item?.start_date, item?.end_date),
      currency: item?.currency || DEFAULT_CURRENCY,
      rateAmount: item?.rate_amount ?? null,
      budget:
        item?.rate_amount !== null && item?.rate_amount !== undefined
          ? formatCurrency(item.rate_amount, item?.currency || DEFAULT_CURRENCY)
          : undefined,
      rate: formatRate(item?.mission_type, item?.rate_amount, item?.currency || DEFAULT_CURRENCY),
      freelancer: formatUserName(item?.freelancer),
      freelancerData: item?.freelancer || null, // Garder les données complètes du freelancer
      creator: formatUserName(item?.created_by),
      approver: formatUserName(item?.approver),
      approver_id:
        item?.approver_id ??
        (item?.approver && typeof item.approver === 'object' ? item.approver.id : null) ??
        null,
      approval_status: item?.approval_status ?? null,
      signor: formatUserName(item?.team_signer),
      team_signer: item?.team_signer || null, // Conservé pour la cloche (vérifier si user === signataire)
      team_signer_id: item?.team_signer_id ?? item?.team_signer?.id ?? null,
      companySignatureSignerIds: getCompanySignatureSignerIds(item),
      company_signature_signer_ids: getCompanySignatureSignerIds(item),
      companySignatureSignerId: item?.company_signature_signer_id ?? item?.companySignatureSignerId ?? null,
      companySignatureSigner: item?.company_signature_signer || item?.companySignatureSigner || null,
      status: rawStatus,
      displayStatus: displayStatus, // camelCase for compatibility
      display_status: displayStatus, // snake_case from backend (keep original)
      purchase_order: item?.purchase_order || null,
      project_code_id: item?.project_code_id || null,
      project_code: item?.project_code || null,
      pending_addenda_count: item?.pending_addenda_count || 0,
      pending_addenda_count_for_company: item?.pending_addenda_count_for_company || 0,
      pending_documents_for_freelancer_count: item?.pending_documents_for_freelancer_count ?? 0,
      pending_documents_for_company_count: item?.pending_documents_for_company_count ?? 0,
      extension_proposed_end_date: item?.extension_proposed_end_date ?? null,
      extensionProposedEndDate: item?.extension_proposed_end_date ?? null,
      start_date: item?.start_date || null,
      end_date: item?.end_date || null,
      start_time: item?.start_time ?? null,
      end_time: item?.end_time ?? null,
      startTime: item?.start_time ?? null,
      endTime: item?.end_time ?? null,
      termination_effective_date: item?.termination_effective_date ?? null,
      termination_contract_end_date: item?.termination_contract_end_date ?? null,
      terminationEffectiveDate: item?.termination_effective_date ?? null,
      terminationContractEndDate: item?.termination_contract_end_date ?? null,
      freelancer_legal_entity_type: item?.freelancer_legal_entity_type ?? null,
      freelancerLegalEntityType: item?.freelancer_legal_entity_type ?? null,
    };
  });

const getTeamMemberOptions = async () => {
  try {
    const response = await fetchMissionTeamMembers();
    const payload = response?.data?.data || {};
    const allMembers = normalizeTeamMemberOptions(payload?.members || []);
    // Filter to only include team-admin for signers
    return allMembers.filter(member => member.role === 'team-admin');
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403 && import.meta.env.DEV) {
      console.debug(
        '[missions] team-members/options: 403 — normal for freelancer / non-team role (hook should use enabled:false)'
      );
    } else {
      console.error('Failed to fetch mission team members:', error);
    }
    return [];
  }
};

const getMissionTemplatePreview = async (missionId, teamSignerId) => {
  if (!missionId || !teamSignerId) return { success: false };
  try {
    const response = await fetchMissionTemplatePreviewApi(missionId, {
      team_signer_id: teamSignerId,
    });
    const payload = response?.data?.data ?? response?.data ?? response;
    const previewUrl = payload?.file_url || payload?.template_url || payload?.url || payload?.document_url || payload?.data?.file_url || payload?.data?.template_url;
    return {
      success: true,
      previewUrl,
      data: payload,
    };
  } catch (error) {
    console.error('Failed to fetch mission template preview:', error);
    throw error;
  }
};

export const missionService = {
  // Get missions with server-like filtering and pagination
  getMissions: async ({
    page = 1,
    pageSize = 10,
    search,
    status,
    startDateFrom,
    createdAtFrom,
    createdAtTo,
    freelancerId,
    teamId,
    sortBy,
    sortDirection,
    projectCodeId,
    hasProjectCode,
    hasPurchaseOrder,
    missionType,
    jobTitle,
    teamTagIds,
    teamUnitIds,
    kycComplete,
    masterAgreement,
    umbrella,
    isFavorite,
    startingSoonAlert,
    forNotifications = false,
  } = {}) => {
    try {
      const params = {
        page,
        per_page: pageSize,
        search,
        start_date_from: startDateFrom,
        freelancer_id: freelancerId,
      };
      if (createdAtFrom) {
        params.created_at_from = createdAtFrom;
      }
      if (createdAtTo) {
        params.created_at_to = createdAtTo;
      }
      if (projectCodeId) {
        params.project_code_id = projectCodeId;
      }
      if (hasProjectCode) {
        params.has_project_code = hasProjectCode;
      }
      if (hasPurchaseOrder) {
        params.has_purchase_order = hasPurchaseOrder;
      }
      if (missionType) {
        params.mission_type = missionType;
      }
      if (jobTitle) {
        params.job_title = jobTitle;
      }
      if (teamTagIds && teamTagIds.length > 0) {
        params.team_tag_id = teamTagIds.join(',');
      }
      if (teamUnitIds && teamUnitIds.length > 0) {
        params.team_unit_ids = teamUnitIds.join(',');
      }
      if (kycComplete) {
        params.kyc_complete = kycComplete;
      }
      if (masterAgreement) {
        params.master_agreement = masterAgreement;
      }
      if (umbrella) {
        params.umbrella = umbrella;
      }
      if (isFavorite) {
        params.is_favorite = 'true';
      }
      if (startingSoonAlert === 'with' || startingSoonAlert === 'without') {
        params.starting_soon_alert = startingSoonAlert;
      }

      // Support array of statuses for multi-select filter
      if (status) {
        if (Array.isArray(status) && status.length > 0) {
          params.status = status.join(',');
        } else if (typeof status === 'string') {
          params.status = status;
        }
      }
      
      // Ajouter team_id si fourni pour isoler les données par équipe active
      if (teamId) {
        params.team_id = teamId;
      }
      
      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
      }
      if (sortDirection) {
        params.sort_direction = sortDirection;
      }
      if (forNotifications) {
        params.for_notifications = '1';
      }

      const response = await fetchMissionList(params, forNotifications ? { hideMessageModal: true } : {});

      const payload = response?.data?.data || {};
      const list = normalizeApiMissions(payload.items || []);

      return {
        success: true,
        data: {
          list,
          total: payload.total ?? 0,
          pageSize: payload.per_page ?? pageSize,
          currentPage: payload.current_page ?? page,
        },
      };
    } catch (error) {
      if (!isRequestCancellation(error)) {
        console.error('Failed to fetch missions from API:', error);
      }

      return {
        success: true,
        data: {
          list: [],
          total: 0,
          pageSize,
          currentPage: page,
        },
      };
    }
  },
  getTeamMemberOptions,
  getSignorOptions: getTeamMemberOptions,
  getMissionTemplatePreview,
  getFreelancerMasterAgreement: async (freelancerId) => {
    try {
      const response = await fetchFreelancerMasterAgreement(freelancerId);
      return response?.data || null;
    } catch (error) {
      console.error('Failed to fetch freelancer master agreement', error);
      throw error;
    }
  },
  getMissionDetail: async (missionId) => {
    try {
      const response = await fetchMissionDetail(missionId);
      const payload = response?.data?.data;
      if (!payload) return null;

      const missionType = normalizePricingType(payload.mission_type);
      const feeUnitMap = {
        hourly: 'per_hour',
        daily: 'per_day',
        monthly: 'per_month',
        deliverable: 'per_deliverable',
      };

      const formatUserInfo = (user) => {
        if (!user) return null;
        const referentName = user?.referent
          ? formatUserName(user.referent)
          : null;
        return {
          id: user?.id,
          name: formatUserName(user),
          email: user?.email || '',
          role: user?.role || '',
          legalEntityType: user?.legal_entity_type || null,
          legal_entity_type: user?.legal_entity_type || null,
          referentName: referentName,
          // Garder aussi les données brutes pour les signatures
          first_name: user?.first_name,
          last_name: user?.last_name,
        };
      };

      return {
        id: payload.id,
        name: payload.name,
        description: payload.description,
        type: missionType,
        contractType: payload?.contract_type || CONTRACT_TYPE_SERVICES_AGREEMENT,
        currency: payload?.currency || DEFAULT_CURRENCY,
        fee: {
          amount: payload?.rate_amount ?? null,
          unit: feeUnitMap[missionType],
          currency: payload?.currency || DEFAULT_CURRENCY,
        },
        deliverables: payload?.deliverables || [],
        startDate: payload?.start_date,
        endDate: payload?.end_date,
        startTime: payload?.start_time ?? null,
        endTime: payload?.end_time ?? null,
        start_time: payload?.start_time ?? null,
        end_time: payload?.end_time ?? null,
        payment_terms_days: payload?.payment_terms_days ?? null,
        payment_terms_basis: payload?.payment_terms_basis ?? null,
        paymentTermsDays: payload?.payment_terms_days ?? null,
        paymentTermsBasis: payload?.payment_terms_basis ?? null,
        status: payload?.status || '',
        // ONLY use display_status from backend - no client-side calculation
        // The backend MUST always return display_status - if not, this is a critical error
        displayStatus: (() => {
          const backendDisplayStatus = payload?.display_status;
          if (!backendDisplayStatus) {
            console.error(
              '[getMissionDetail] CRITICAL: Backend did not provide display_status for mission:',
              payload?.id || 'unknown',
              'Status:', payload?.status,
              'This is a backend bug - display_status should always be calculated server-side.'
            );
            // Use pending-freelancer-review as fallback instead of awaiting-freelancer-acceptance
            return 'pending-freelancer-review';
          }
          return backendDisplayStatus;
        })(),
        display_status: payload?.display_status || 'pending-freelancer-review', // Keep snake_case from backend
        pending_addenda_count: payload?.pending_addenda_count || 0,
        pending_addenda_count_for_company: payload?.pending_addenda_count_for_company || 0,
        pending_documents_for_freelancer_count: payload?.pending_documents_for_freelancer_count ?? 0,
        pending_documents_for_company_count: payload?.pending_documents_for_company_count ?? 0,
        companySignatureSignerIds: getCompanySignatureSignerIds(payload || {}),
        company_signature_signer_ids: getCompanySignatureSignerIds(payload || {}),
        companySignatureSignerId: payload?.company_signature_signer_id ?? null,
        companySignatureSigner: formatUserInfo(payload?.company_signature_signer),
        creator: formatUserName(payload?.created_by),
        createdById: payload?.created_by?.id || null,
        approver: formatUserInfo(payload?.approver),
        approvalStatus: payload?.approval_status || null,
        approval_status: payload?.approval_status || null,
        approvalDecisionAt: payload?.approval_decision_at || null,
        approval_decision_at: payload?.approval_decision_at || null,
        approvalDecidedById: payload?.approval_decided_by_id || null,
        approval_decided_by_id: payload?.approval_decided_by_id || null,
        approvalDecidedBy: formatUserInfo(payload?.approval_decided_by),
        approval_decided_by: payload?.approval_decided_by || null,
        approvalRequired: Boolean(payload?.approval_required),
        createdAt: payload?.created_at,
        updatedAt: payload?.updated_at || null,
        updated_at: payload?.updated_at || null,
        updatedById: payload?.updated_by_id || null,
        updated_by_id: payload?.updated_by_id || null,
        updatedBy: formatUserInfo(payload?.updated_by),
        updated_by: payload?.updated_by || null,
        assignee: formatUserInfo(payload?.freelancer),
        teamSigner:
          formatUserInfo(payload?.team_signer) ||
          formatUserInfo(payload?.company_signature_signer) ||
          (Array.isArray(payload?.company_signature_signers)
            ? formatUserInfo(payload.company_signature_signers[0])
            : null),
        documents: payload?.documents || { extension_documents: [], normal_documents: [] },
        approvalComment: payload?.approval_comment || '',
        is_invitation_freelancer: Boolean(payload?.is_invitation_freelancer),
        rejectionReason: payload?.rejection_reason || null,
        terminationReason: payload?.termination_reason || null,
        terminatedAt: payload?.terminated_at || null,
        terminatedBy: formatUserInfo(payload?.terminated_by),
        terminated_by: payload?.terminated_by || null,
        terminationEffectiveDate: payload?.termination_effective_date || null,
        terminationContractEndDate: payload?.termination_contract_end_date || null,
        scheduledTerminationMaxEndDate: payload?.scheduled_termination_max_end_date || null,
        purchase_order: payload?.purchase_order || null,
        location: payload?.location || null,
        external_id: payload?.external_id || null,
        project_code_id: payload?.project_code_id || null,
        project_code: payload?.project_code || null,
        project_codes: Array.isArray(payload?.project_codes) ? payload.project_codes : [],
        addenda: (payload?.mission_addenda || []).map((addendum) => ({
          ...addendum,
          created_by: addendum?.created_by ?? null,
          createdByName: formatUserName(addendum?.created_by),
          cancelled_by: addendum?.cancelled_by ?? null,
          cancelledByName: formatUserName(addendum?.cancelled_by),
        })),
        allow_supplementary_items: Boolean(payload?.allow_supplementary_items),
        require_carbon_footprint: Boolean(payload?.require_carbon_footprint),
        require_mission_feedback: Boolean(payload?.require_mission_feedback),
        allow_expense_reports: Boolean(payload?.allow_expense_reports),
        company_billing_info: payload?.company_billing_info || null,
        max_billable_amount_excl_vat:
          payload?.max_billable_amount_excl_vat != null && payload?.max_billable_amount_excl_vat !== ''
            ? Number(payload.max_billable_amount_excl_vat)
            : null,
        max_total_envelope:
          payload?.max_total_envelope != null && payload?.max_total_envelope !== ''
            ? Number(payload.max_total_envelope)
            : null,
        client_rate_amount:
          payload?.client_rate_amount != null && payload?.client_rate_amount !== ''
            ? Number(payload.client_rate_amount)
            : null,
        external_referent_contact_id: payload?.external_referent_contact_id || null,
        external_referent_contact: payload?.external_referent_contact || null,
        has_client_contract: Boolean(payload?.has_client_contract),
        hasClientContract: Boolean(payload?.has_client_contract),
        last_client_contract_reminder_sent_at: payload?.last_client_contract_reminder_sent_at || null,
        lastClientContractReminderSentAt: payload?.last_client_contract_reminder_sent_at || null,
        last_freelancer_pending_reminder_sent_at:
          payload?.last_freelancer_pending_reminder_sent_at || null,
        lastFreelancerPendingReminderSentAt:
          payload?.last_freelancer_pending_reminder_sent_at || null,
        client_contract_signer_contact_id: payload?.client_contract_signer_contact_id || null,
        clientContractSignerContactId: payload?.client_contract_signer_contact_id || null,
        client_contract_signer_contact: payload?.client_contract_signer_contact || null,
        clientContractSignerContact: payload?.client_contract_signer_contact || null,
        referentInfo: payload?.freelancer?.referent
          ? {
              id: payload.freelancer.referent.id,
              first_name: payload.freelancer.referent.first_name || '',
              last_name: payload.freelancer.referent.last_name || '',
              email: payload.freelancer.referent.email || '',
            }
          : null,
        freelancer_legal_entity_type:
          payload?.freelancer_legal_entity_type ??
          payload?.freelancer?.legal_entity_type ??
          null,
        portage_company: payload?.portage_company ?? null,
        portage_affiliation: payload?.portage_affiliation ?? null,
        portage_contact:
          payload?.portage_contact ??
          payload?.portage_affiliation?.contact ??
          null,
      };
    } catch (error) {
      // Ne pas avaler les 403/404 : la fiche mission classe l'échec pour afficher
      // l'écran « introuvable » (sinon null → MissionDetailUnavailableError → « Réessayer »).
      console.error('Failed to fetch mission detail:', error);
      throw error;
    }
  },
  getMissionEnvelopeUsage: async (missionId, { excludeReportId } = {}) => {
    try {
      const params = excludeReportId ? { exclude_report_id: excludeReportId } : {};
      const response = await fetchMissionEnvelopeUsage(missionId, params);
      return response?.data?.data ?? null;
    } catch (error) {
      console.error('Failed to fetch mission envelope usage:', error);
      throw error;
    }
  },
  createMission: async (payload) => {
    try {
      const response = await createMissionApi(payload, { hideMessageModal: true });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to create mission:', error);
      throw error;
    }
  },
  updateMission: async (missionId, payload) => {
    try {
      const response = await updateMissionApi(missionId, payload);
      return response?.data?.data ?? response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to update mission:', error);
      throw error;
    }
  },
  getMissionAssigneeOptions: async () => {
    try {
      // Récupérer tous les freelancers disponibles
      const response = await fetchMissionFreelancers();
      const payload = response?.data?.data ?? response?.data;
      const freelancers = payload?.freelancers || [];

      // Convertir les données freelancer au format d'options
      const options = freelancers.map((item) => ({
        id: item?.id,
        name: formatUserName(item),
        email: item?.email ?? '',
        role: item?.role ?? 'Freelancer',
        assignmentStatus: item?.is_on_mission ? 'staffed' : 'unstaffed',
        status: item?.is_on_mission ? 'staffed' : 'unstaffed',
      }));

      return options;
    } catch (error) {
      console.error('Failed to fetch mission assignee options:', error);
      return [];
    }
  },
  getFreelancerOptions: async () => {
    try {
      const response = await fetchMissionFreelancers();
      const payload = response?.data?.data ?? response?.data;
      const list = normalizeFreelancerOptions(payload?.freelancers || []);
      return list;
    } catch (error) {
      console.error('Failed to fetch freelancer options:', error);
      return [];
    }
  },
  updateMissionAssignment: async (missionId, assigneeId) => {
    try {
      const response = await assignFreelancerApi(missionId, assigneeId);
      const payload = response?.data?.data;
      if (!payload) return { success: true };

      // Retourner les informations de l'assigné mises à jour
      return {
        success: true,
        assignee: {
          id: payload?.freelancer?.id,
          name: formatUserName(payload?.freelancer),
          email: payload?.freelancer?.email || '',
          role: payload?.freelancer?.role || 'Freelancer',
        },
      };
    } catch (error) {
      console.error('Failed to update mission assignment:', error);
      throw error;
    }
  },
  deleteMission: async (missionId) => {
    try {
      const response = await deleteMissionApi(missionId);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to delete mission:', error);
      throw error;
    }
  },

  // Convertir le format des données de documents (seulement la conversion de format, n'appelle pas l'API)
  getMissionDocuments: (rawDocuments = []) => {
    try {
      if (!Array.isArray(rawDocuments)) return [];

      // Convertir les données de documents retournées par le backend au format requis par le frontend
      return rawDocuments.map((doc) => {
        // Normalize signature_status: API returns kebab-case, constant is UPPER_SNAKE_CASE
        let normalizedSignatureStatus = doc.signature_status;
        if (normalizedSignatureStatus) {
          normalizedSignatureStatus = String(normalizedSignatureStatus)
            .trim()
            .toUpperCase()
            .replace(/-/g, '_');
        }

        return {
        id: doc.id,
        title: doc.title,
        fileName: doc.file_name,
        fileKey: doc.file_key,
        fileUrl: doc.file_url,
        uploadDate: doc.created_at || new Date().toISOString(),
        documentKind: doc.document_kind,
        requiresSignature: doc.requires_signature,
          signatureStatus: normalizedSignatureStatus,
        teamSignerId: doc.team_signer_id,
        freelancerSignedAt: doc.freelancer_signed_at,
        freelancerRejectedAt: doc.freelancer_rejected_at,
        teamSignedAt: doc.team_signed_at,
        teamRejectedAt: doc.team_rejected_at,
        rejectionReason: doc.rejection_reason,
        orderIndex: doc.order_index,
        freelancerSignedFileKey: doc.freelancer_signed_file_key,
        teamSignedFileKey: doc.team_signed_file_key,
        extensionNewEndDate: doc.extension_new_end_date,
        masterAgreementValidUntil: doc.master_agreement_valid_until || null,
        masterAgreementId: doc.master_agreement_id || null,
        isMasterAgreement: Boolean(doc.is_master_agreement),
        };
      });
    } catch (error) {
      console.error('Failed to transform mission documents:', error);
      return [];
    }
  },

  // Supprimer un document
  deleteDocument: async (missionId, documentId) => {
    try {
      const response = await deleteDocumentApi(missionId, documentId);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to delete document:', error);
      throw error;
    }
  },

  // Téléverser les documents de mission
  uploadMissionDocuments: async (missionId, documentData) => {
    try {
      const response = await uploadMissionDocumentsApi(missionId, documentData);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to upload mission documents:', error);
      throw error;
    }
  },

  // Téléverser d'autres documents (other-documents)
  uploadOtherDocuments: async (missionId, documentData) => {
    try {
      const response = await uploadOtherDocumentsApi(missionId, documentData);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to upload other documents:', error);
      throw error;
    }
  },

  // Éditer les documents de mission (mettre à jour les documents existants)
  editMissionDocuments: async (missionId, documentData) => {
    try {
      const response = await editMissionDocumentsApi(missionId, documentData);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to edit mission documents:', error);
      throw error;
    }
  },

  // Refuser un document
  rejectDocumentAsFreelancer: async (missionId, documentId, reason) => {
    try {
      const response = await rejectSignableApi({
        signable_type: 'mission_document',
        signable_id: documentId,
        mission_id: missionId,
        reason,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject document as freelancer:', error);
      throw error;
    }
  },

  // Refuser le Contrat Cadre en tant que freelancer
  rejectMasterAgreementAsFreelancer: async (missionId, reason) => {
    try {
      const response = await rejectSignableApi({
        signable_type: 'master_agreement',
        signable_id: missionId, // Pour le master agreement, on utilise mission_id
        mission_id: missionId,
        reason,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject master agreement as freelancer:', error);
      throw error;
    }
  },

  // Refuser le Contrat Cadre en tant que team
  rejectMasterAgreementAsTeam: async (missionId, reason) => {
    try {
      const response = await rejectSignableApi({
        signable_type: 'master_agreement',
        signable_id: missionId,
        mission_id: missionId,
        reason,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject master agreement as team:', error);
      throw error;
    }
  },

  // Refuser un document en tant que team
  rejectDocumentAsTeam: async (missionId, documentId, reason) => {
    try {
      const response = await rejectSignableApi({
        signable_type: 'mission_document',
        signable_id: documentId,
        mission_id: missionId,
        reason,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject document as team:', error);
      throw error;
    }
  },

  // Prolonger la mission
  extendMission: async (missionId, extendData) => {
    try {
      // Toujours utiliser JSON - le backend génère le PDF ou traite les documents uploadés
      const requestData = {
        new_end_date: extendData.new_end_date,
        extend_without_contract: extendData.extend_without_contract || false,
      };

      if (extendData.signer_id) {
        requestData.team_signer_id = extendData.signer_id;
      }

      if (extendData.template_id) {
        requestData.template_id = extendData.template_id;
      }

      const response = await extendMissionApi(missionId, requestData);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to extend mission:', error);
      throw error;
    }
  },

  // Terminer la mission de manière anticipée (date effective en YYYY-MM-DD)
  terminateMission: async (missionId, reason, effectiveEndDate) => {
    try {
      const newEndDate = effectiveEndDate || dayjs().format('YYYY-MM-DD');
      const response = await terminateMissionApi(missionId, {
        reason,
        new_end_date: newEndDate,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to terminate mission:', error);
      throw error;
    }
  },

  updateScheduledTerminationDate: async (missionId, newEndDate, reason) => {
    try {
      const payload = { new_end_date: newEndDate };
      if (reason !== undefined) {
        payload.reason = reason;
      }
      const response = await patchScheduledTerminationApi(missionId, payload);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to update scheduled termination date:', error);
      throw error;
    }
  },

  // Freelancer accepte la mission
  acceptMission: async (missionId) => {
    try {
      const response = await acceptMissionApi(missionId);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to accept mission:', error);
      throw error;
    }
  },

  // Admin approuve ou refuse une mission en attente de validation
  reviewMissionApproval: async (missionId, { action, comment }) => {
    try {
      const response = await reviewMissionApprovalApi(missionId, { action, comment });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to review mission approval:', error);
      throw error;
    }
  },

  // Freelancer refuse la mission
  rejectMission: async (missionId, reason) => {
    try {
      const response = await rejectMissionApi(missionId, { reason });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject mission:', error);
      throw error;
    }
  },

  // Entreprise annule la mission (avant signature freelance)
  cancelMission: async (missionId) => {
    try {
      const response = await cancelMissionApi(missionId);
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to cancel mission:', error);
      throw error;
    }
  },

  // Freelancer accepte un addendum
  acceptAddendum: async (missionId, addendumId) => {
    try {
      const response = await acceptAddendumApi(missionId, addendumId, {
        action: 'accept',
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to accept addendum:', error);
      throw error;
    }
  },

  // Freelancer refuse un addendum
  rejectAddendum: async (missionId, addendumId, reason) => {
    try {
      const response = await rejectAddendumApi(missionId, addendumId, {
        reason,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to reject addendum:', error);
      throw error;
    }
  },

  // Entreprise retire une proposition de prolongation
  cancelAddendumProposal: async (missionId, addendumId, reason) => {
    try {
      const response = await cancelAddendumApi(missionId, addendumId, {
        reason: reason || undefined,
      });
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Failed to cancel addendum proposal:', error);
      throw error;
    }
  },

  // Initier une signature YouSign
  initiateSignatureRequest: async (signableType, signableId, missionId, signerRole) => {
    try {
      const response = await initiateSignatureRequestApi({
        signable_type: signableType,
        signable_id: signableId,
        mission_id: missionId,
        signer_role: signerRole,
      });
      return response?.data?.data;
    } catch (error) {
      console.error('Failed to initiate signature request:', error);
      throw error;
    }
  },
};

export default missionService;
